import { ChangeEvent, FC, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { Box } from '@mui/material';
import { t } from 'i18next';
import { debounce } from 'lodash';
import { usePostUserDetails } from 'services/users/userDetails/hooks';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser, getSetAuthUser } from 'store/useUserStore/selectors';
import { IAuthUser } from 'store/useUserStore/types';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { SectionWrapper } from 'components/_basic/SectionWrapper/SectionWrapper';

import { personalDetailsInputMask } from 'helpers/personalDetailsInputMask';
import ToastService from 'helpers/ToastService';

import { IAccountPersonalInfoForm } from './types';

import styles from './AccountSettings.module.scss';

const BLUR_DELAY = 2000;

export const AccountPersonalInfo: FC = () => {
  const changeUserDetailsMutation = usePostUserDetails();
  const authUser = useUserStore(getAuthUser) as IAuthUser;
  const setUser = useUserStore(getSetAuthUser);

  const {
    formState: { isDirty },
    control,
    setValue,
    getValues,
    reset,
  } = useForm<IAccountPersonalInfoForm>({
    defaultValues: { firstName: authUser?.firstName ?? '', lastName: authUser?.lastName ?? '' },
  });

  const inputChangeHandler = ({ target: { value, name } }: ChangeEvent<HTMLInputElement>) => {
    if (name === 'firstName' || name === 'lastName') {
      setValue(name, personalDetailsInputMask(value), { shouldTouch: true, shouldDirty: true });
    }
  };

  const debouncedInputBlur = useCallback(
    debounce(async () => {
      try {
        if (isDirty) {
          const { firstName, lastName } = getValues();
          const result = await changeUserDetailsMutation.mutateAsync({ firstName, lastName });

          setUser({ ...authUser, ...result });
          ToastService.showSuccess('Personal details updated successfully');
        }
      } catch (error) {
        error instanceof Error && ToastService.showError(error.message);
      }
    }, BLUR_DELAY),
    [isDirty, getValues, changeUserDetailsMutation, setUser, authUser, reset],
  );

  return (
    <SectionWrapper sectionTitle={t('personalInfo')}>
      <Box component="form" className={styles.personalInfoWrapper}>
        <TextInputControl
          fieldClassName={styles.personalInfoInput}
          name="firstName"
          control={control}
          label={t('company.general.firstName')}
          inputProps={{ maxLength: 255 }}
          onChange={inputChangeHandler}
          onBlur={debouncedInputBlur}
        />
        <TextInputControl
          fieldClassName={styles.personalInfoInput}
          name="lastName"
          control={control}
          label={t('company.general.lastName')}
          inputProps={{ maxLength: 255 }}
          onChange={inputChangeHandler}
          onBlur={debouncedInputBlur}
        />
      </Box>
    </SectionWrapper>
  );
};
